import AddCircleOutlined from "@mui/icons-material/AddCircleOutlined";
import DeleteOutlined from "@mui/icons-material/DeleteOutlined";
import MoreTimeOutlined from "@mui/icons-material/MoreTimeOutlined";
import { Box, InputAdornment, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField } from "@mui/material";
import { type FC, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import { useAllJobs } from "../../../hooks/useJobs";
import { useRoleColors } from "../../../hooks/useRoleColors";
import { useGetDepartmentNurseUnions } from "../../../pages/Profile/v2/hooks";
import { normalizeSelectedJobIds, selectedJobIdsForDisplay } from "../../../utils/jobSelection";
import type { DisplayEmploymentType, OvertimeConfig } from "../../../utils/models";
import { ConsecutiveWindow, EmploymentType, NurseFullRole, NurseRole, OvertimeConfigType, RateHoursBonusType } from "../../../utils/models";
import { fromDisplayEmploymentType, getEmploymentTypeDisplayName, nurseRoleDisplayName, toDisplayEmploymentType } from "../../../utils/utilFunctions";
import { SystemFlatSwitch } from "../../UIToolkit/Clickable/SystemFlatSwitch";
import { SystemDropdown } from "../../UIToolkit/Inputs/SystemDropDown";
import { EmptyState } from "../../UIToolkit/molecules/EmptyState";
import { SettingsSection } from "../../UIToolkit/molecules/SettingsSection";
import SystemButton from "../../UIToolkit/SystemButton/SystemButton";
import { ConfigMultiSelectCell } from "../../UIToolkit/Tables/ConfigTable/ConfigMultiSelectCell";
import {
	BORDERLESS_INPUT_SX,
	BORDERLESS_SELECT_SX,
	CONFIG_TABLE_BODY_CELL_SX,
	CONFIG_TABLE_CONTAINER_SX,
	CONFIG_TABLE_HEADER_CELL_SX,
	configTableBodyRowSx,
} from "../../UIToolkit/Tables/ConfigTable/configTableSx";
import { ConfigUnionCell } from "./ConfigUnionCell";
import { actionsColumn, columnWidthSx, OVERTIME_COLUMNS, visibleColumns } from "./wageRuleColumns";
import type { WageRulesTableProps } from "./wageRulesTableProps";

const OVERTIME_ACTIONS_COLUMN = actionsColumn("department.config.rates.overtime.col.actions");

/** Use shared display names from utilFunctions for consistency with Profile and rest of app. */
const EMPLOYMENT_TYPE_OPTIONS: { value: DisplayEmploymentType; label: string; selectedLabel: string }[] = (
	Object.values(EmploymentType) as EmploymentType[]
).map((et) => ({
	value: toDisplayEmploymentType(et),
	label: getEmploymentTypeDisplayName(et),
	selectedLabel: getEmploymentTypeDisplayName(et),
}));

/**
 * The overtime rules table, edited on Team → Wage Settings (DAT-905). Moved from Settings →
 * Department Configuration, where it wrote through the whole-department config save.
 */
export const OvertimeConfigTable: FC<WageRulesTableProps<OvertimeConfig>> = ({
	rows: overtimeConfigs,
	setRows,
	roles,
	jobIds,
	departmentId,
	disabled,
	highlightedRowIndices,
}) => {
	const roleColor = useRoleColors();
	const { t } = useTranslation();
	const bonusTypeOptions = useMemo(
		() => [
			{ value: RateHoursBonusType.FlatRate, label: t("department.config.rates.wageMode.flatRate"), disabled },
			{ value: RateHoursBonusType.Percentage, label: t("department.config.rates.wageMode.percentage"), disabled },
		],
		[t, disabled],
	);
	const { data: existingUnions } = useGetDepartmentNurseUnions();
	const { data: allJobs } = useAllJobs();
	const departmentJobs = useMemo(() => {
		const selected = new Set(jobIds ?? []);
		return (allJobs ?? []).filter((j) => selected.has(j.id));
	}, [allJobs, jobIds]);
	const showJobColumn = departmentJobs.length > 0;
	const columns = useMemo(() => [...visibleColumns(OVERTIME_COLUMNS, showJobColumn), OVERTIME_ACTIONS_COLUMN], [showJobColumn]);
	// Always shown, unlike the cost-report column. This is the screen you come to IN ORDER to create
	// a consecutive rule, so hiding the fields until one exists makes the feature look missing — and
	// columns appearing mid-edit is jumpy. Per-row blanking (below) keeps them quiet for other types,
	// the same way `showJobColumn` handles jobs.
	const isConsecutive = useCallback((row: OvertimeConfig) => row.type === OvertimeConfigType.consecutive_day, []);
	// Switching type must not leave stale day-range values behind: the backend validator rejects a
	// daily/weekly row that still carries them, so clear on the way out and seed a default on the way in.
	const buildTypeChange = useCallback(
		(type: OvertimeConfigType): Partial<OvertimeConfig> =>
			type === OvertimeConfigType.consecutive_day
				? { type, consecutiveWindow: ConsecutiveWindow.work_week, consecutiveDaysFrom: null, consecutiveDaysTo: null }
				: { type, consecutiveWindow: null, consecutiveDaysFrom: null, consecutiveDaysTo: null },
		[],
	);
	// `Number("")` is 0 and `Number("abc")` is NaN, neither of which is a day count. Unparseable input
	// is treated as empty; a real-but-invalid number like 1.5 is KEPT so `getOvertimeConfigReasons`
	// can explain why Save is disabled instead of the field silently resetting under the user.
	const parseDayCount = useCallback((raw: string): number | null => {
		if (raw === "") return null;
		const parsed = Number(raw);
		return Number.isFinite(parsed) ? parsed : null;
	}, []);

	const addRow = useCallback(() => {
		setRows((prev) => [
			...prev,
			{
				id: uuidv4(),
				name: "",
				hoursLimit: 0,
				roles: [],
				jobIds: [],
				employmentType: [] as string[],
				union: [] as string[],
				isUnion: null as boolean | null,
				type: OvertimeConfigType.weekly,
				consecutiveWindow: null,
				consecutiveDaysFrom: null,
				consecutiveDaysTo: null,
				rate: 0,
				rateType: RateHoursBonusType.FlatRate,
				departmentId,
			} as OvertimeConfig,
		]);
	}, [setRows, departmentId]);

	const removeRow = useCallback(
		(index: number) => {
			setRows((prev) => prev.filter((_, i) => i !== index));
		},
		[setRows],
	);

	const updateRow = useCallback(
		(index: number, patch: Partial<OvertimeConfig>) => {
			setRows((prev) => {
				const list = [...prev];
				list[index] = { ...list[index], ...patch };
				return list;
			});
		},
		[setRows],
	);

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: "1rem", width: "100%" }}>
			<SettingsSection
				headerOnly
				icon={<MoreTimeOutlined />}
				title={t("department.config.rates.overtime.section")}
				dataTestId="dept-config-rates-overtime-header"
				action={
					<SystemButton
						buttonVariant="primary"
						size="small"
						leadingIcon={<AddCircleOutlined />}
						onClick={addRow}
						disabled={disabled}
						data-testid="dept-config-rates-overtime-add-row"
					>
						{t("department.config.rates.addRow")}
					</SystemButton>
				}
			/>
			<TableContainer component={Paper} sx={CONFIG_TABLE_CONTAINER_SX}>
				<Table sx={{ minWidth: "max-content", width: "100%" }}>
					<TableHead>
						<TableRow>
							{columns.map((column) => (
								<TableCell key={column.id} sx={[CONFIG_TABLE_HEADER_CELL_SX, columnWidthSx(column)]}>
									{t(column.headerKey)}
								</TableCell>
							))}
						</TableRow>
					</TableHead>
					<TableBody>
						{overtimeConfigs.map((row, index) => (
							<TableRow
								key={row.id ?? index}
								sx={configTableBodyRowSx(highlightedRowIndices.has(index))}
								data-testid={`dept-config-rates-overtime-row-${index}`}
								data-highlighted={highlightedRowIndices.has(index) ? "true" : undefined}
							>
								<TableCell sx={CONFIG_TABLE_BODY_CELL_SX}>
									<TextField
										size="small"
										disabled={disabled}
										fullWidth
										sx={BORDERLESS_INPUT_SX}
										value={row.name}
										onChange={(e) => updateRow(index, { name: e.target.value })}
										placeholder={t("department.config.rates.overtime.col.name")}
										slotProps={{ htmlInput: { "data-testid": `dept-config-rates-overtime-name-${index}` } }}
									/>
								</TableCell>
								<TableCell sx={CONFIG_TABLE_BODY_CELL_SX}>
									<TextField
										type="number"
										size="small"
										disabled={disabled}
										fullWidth
										sx={BORDERLESS_INPUT_SX}
										value={row.hoursLimit ?? ""}
										onChange={(e) => updateRow(index, { hoursLimit: e.target.value === "" ? 0 : Number(e.target.value) })}
										slotProps={{
											htmlInput: { min: 0, step: 0.01, "data-testid": `dept-config-rates-overtime-hours-limit-${index}` },
										}}
									/>
								</TableCell>
								<TableCell sx={CONFIG_TABLE_BODY_CELL_SX}>
									<ConfigMultiSelectCell<NurseRole>
										disabled={disabled}
										ariaLabel={t("department.config.rates.overtime.col.role")}
										dataTestId={`dept-config-rates-overtime-roles-${index}`}
										items={roles.map((role) => ({
											value: role,
											label: NurseFullRole[role],
											selectedLabel: nurseRoleDisplayName[role] ?? role,
											color: roleColor(NurseFullRole[role]).dot,
										}))}
										selectedItems={(row.roles ?? []) as NurseRole[]}
										onChangeMultiple={(selectedValues) => {
											const nurseRoles = selectedValues.filter(
												(v): v is NurseRole => Object.keys(NurseRole).includes(String(v)) && roles.includes(v as NurseRole),
											) as NurseRole[];
											updateRow(index, { roles: nurseRoles.length === roles.length ? roles : nurseRoles });
										}}
										errorText={(row.roles == null || row.roles?.length === 0) && roles.length > 0 ? t("department.config.rates.chooseAllOrRoles") : undefined}
									/>
								</TableCell>
								{showJobColumn && (
									<TableCell sx={CONFIG_TABLE_BODY_CELL_SX}>
										<ConfigMultiSelectCell<string>
											disabled={disabled}
											ariaLabel={t("department.config.rates.overtime.col.job")}
											dataTestId={`dept-config-rates-overtime-jobs-${index}`}
											items={departmentJobs.map((j) => ({ value: j.id, label: j.displayName ?? j.name, selectedLabel: j.displayName ?? j.name }))}
											selectedItems={selectedJobIdsForDisplay(row.jobIds, departmentJobs)}
											onChangeMultiple={(selectedValues) => {
												updateRow(index, { jobIds: normalizeSelectedJobIds(selectedValues as string[], departmentJobs) });
											}}
										/>
									</TableCell>
								)}
								<TableCell sx={CONFIG_TABLE_BODY_CELL_SX}>
									<ConfigMultiSelectCell<DisplayEmploymentType>
										disabled={disabled}
										ariaLabel={t("department.config.rates.overtime.col.employmentType")}
										dataTestId={`dept-config-rates-overtime-employment-types-${index}`}
										items={EMPLOYMENT_TYPE_OPTIONS}
										selectedItems={((row.employmentType ?? []) as EmploymentType[]).map(toDisplayEmploymentType)}
										onChangeMultiple={(selectedValues) => {
											const displayValues = (selectedValues as string[]).filter((v): v is DisplayEmploymentType =>
												EMPLOYMENT_TYPE_OPTIONS.some((opt) => opt.value === v),
											);
											const types = displayValues.map(fromDisplayEmploymentType);
											updateRow(index, {
												employmentType: types.length === EMPLOYMENT_TYPE_OPTIONS.length ? (Object.values(EmploymentType) as EmploymentType[]) : types,
											});
										}}
										errorText={
											(row.employmentType == null || row.employmentType?.length === 0) && EMPLOYMENT_TYPE_OPTIONS.length > 0
												? t("department.config.rates.chooseAllOrEmploymentTypes")
												: undefined
										}
									/>
								</TableCell>
								<TableCell sx={CONFIG_TABLE_BODY_CELL_SX}>
									<ConfigUnionCell
										ariaLabel={t("department.config.rates.overtime.col.union")}
										dataTestId={`dept-config-rates-overtime-union-${index}`}
										isUnion={row.isUnion}
										union={row.union}
										existingUnions={existingUnions}
										disabled={disabled}
										onChange={(patch) => updateRow(index, patch)}
									/>
								</TableCell>
								<TableCell sx={CONFIG_TABLE_BODY_CELL_SX}>
									<SystemDropdown<OvertimeConfigType>
										fullWidth
										selectSx={BORDERLESS_SELECT_SX}
										disabled={disabled}
										data-testid={`dept-config-rates-overtime-type-${index}`}
										items={[
											{ value: OvertimeConfigType.weekly, label: t("department.config.rates.overtime.type.weekly") },
											{ value: OvertimeConfigType.daily, label: t("department.config.rates.overtime.type.daily") },
											{ value: OvertimeConfigType.all, label: t("department.config.rates.option.all") },
											{ value: OvertimeConfigType.consecutive_day, label: t("department.config.rates.overtime.type.consecutiveDay") },
										]}
										selectedItem={row.type ?? OvertimeConfigType.weekly}
										onChange={(value) => {
											// buildTypeChange, not `{ type: value }`: switching a consecutive row back to daily must
											// clear the day-range fields, or the backend validator rejects the row on save.
											if (value !== undefined) updateRow(index, buildTypeChange(value));
										}}
									/>
								</TableCell>
								{
									<>
										<TableCell sx={CONFIG_TABLE_BODY_CELL_SX}>
											{isConsecutive(row) && (
												<TextField
													type="number"
													size="small"
													disabled={disabled}
													fullWidth
													sx={BORDERLESS_INPUT_SX}
													value={row.consecutiveDaysFrom ?? ""}
													onChange={(e) => updateRow(index, { consecutiveDaysFrom: parseDayCount(e.target.value) })}
													slotProps={{ htmlInput: { min: 1, step: 1, "data-testid": `dept-config-rates-overtime-days-from-${index}` } }}
												/>
											)}
										</TableCell>
										<TableCell sx={CONFIG_TABLE_BODY_CELL_SX}>
											{isConsecutive(row) && (
												<TextField
													type="number"
													size="small"
													disabled={disabled}
													fullWidth
													sx={BORDERLESS_INPUT_SX}
													value={row.consecutiveDaysTo ?? ""}
													placeholder={t("department.config.rates.overtime.daysTo.onward")}
													onChange={(e) => updateRow(index, { consecutiveDaysTo: parseDayCount(e.target.value) })}
													slotProps={{ htmlInput: { min: 1, step: 1, "data-testid": `dept-config-rates-overtime-days-to-${index}` } }}
												/>
											)}
										</TableCell>
									</>
								}
								<TableCell sx={CONFIG_TABLE_BODY_CELL_SX}>
									<TextField
										type="number"
										size="small"
										disabled={disabled}
										fullWidth
										sx={BORDERLESS_INPUT_SX}
										value={row.rate ?? ""}
										onChange={(e) => updateRow(index, { rate: e.target.value === "" ? 0 : Number(e.target.value) })}
										slotProps={{
											input: {
												startAdornment: <InputAdornment position="start">{row.rateType === RateHoursBonusType.Percentage ? "%" : "$"}</InputAdornment>,
											},

											htmlInput: { min: 0, step: 0.01, "data-testid": `dept-config-rates-overtime-rate-${index}` },
										}}
									/>
								</TableCell>
								<TableCell sx={CONFIG_TABLE_BODY_CELL_SX}>
									<Box sx={{ minWidth: 160, maxWidth: 200 }}>
										<SystemFlatSwitch<RateHoursBonusType>
											options={bonusTypeOptions}
											activeIndexOrValue={row.rateType ?? RateHoursBonusType.FlatRate}
											onSwitch={(option) => updateRow(index, { rateType: option.value })}
											delayCallback={0}
											rootWidth="100%"
											data-testid={`dept-config-rates-overtime-rate-type-${index}`}
										/>
									</Box>
								</TableCell>
								<TableCell sx={CONFIG_TABLE_BODY_CELL_SX}>
									<SystemButton
										buttonVariant="borderless"
										size="small"
										leadingIcon={<DeleteOutlined fontSize="small" />}
										onClick={() => removeRow(index)}
										disabled={disabled}
										tooltip={t("department.config.rates.delete")}
										aria-label={t("department.config.rates.delete")}
										data-testid={`dept-config-rates-overtime-delete-${index}`}
									/>
								</TableCell>
							</TableRow>
						))}
						{overtimeConfigs.length === 0 && (
							<TableRow>
								<TableCell colSpan={columns.length} sx={{ borderBottom: "none", p: 0 }}>
									<EmptyState
										icon={<MoreTimeOutlined />}
										title={t("department.config.rates.overtime.empty.title")}
										hint={t("department.config.rates.overtime.empty.hint")}
										action={
											<SystemButton
												buttonVariant="secondary"
												size="small"
												leadingIcon={<AddCircleOutlined fontSize="small" />}
												onClick={addRow}
												disabled={disabled}
												data-testid="dept-config-rates-overtime-empty-add"
											>
												{t("department.config.rates.addRow")}
											</SystemButton>
										}
										dataTestId="dept-config-rates-overtime-empty"
									/>
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</TableContainer>
		</Box>
	);
};
